import { useCallback } from 'react';
import { FileUploadProperty } from 'shared';
import {
  useUpdateFileUploadItemStateDispatch,
  updateFileUploadItemState,
} from 'store';
import {
  DeleteFromTemporaryPayload,
  deleteFromTemporary,
} from './deleteFromTemporary';
import { FileUploadResult, FILE_UPLOAD_RESULT_TYPE } from './models';
import { uploadAndVerify, UploadAndVerifyPayload } from './uploadAndVerify';

type UploadAndVerifyFilePayload = Pick<
  UploadAndVerifyPayload,
  'file' | 'session' | 'setting'
>;
type DeleteFromTemporaryFilePayload = Pick<
  DeleteFromTemporaryPayload,
  'session' | 'setting'
>;

type UseFileUploadEventHandlerReturnType = {
  uploadAndVerifyFile: (
    payload: UploadAndVerifyFilePayload,
  ) => Promise<FileUploadResult>;
  deleteFromTemporaryFile: (
    payload: DeleteFromTemporaryFilePayload,
  ) => Promise<FileUploadResult>;
};

export const useFileUploadEventHandler = (
  property: FileUploadProperty,
): UseFileUploadEventHandlerReturnType => {
  const { subjectId } = property.inputFormat;
  const dispatch = useUpdateFileUploadItemStateDispatch();
  const uploadAndVerifyFile: UseFileUploadEventHandlerReturnType['uploadAndVerifyFile'] =
    useCallback(
      async ({ file, setting, session }) => {
        dispatch(
          updateFileUploadItemState({
            subjectId,
            awaiting: true,
          }),
        );
        const result = await uploadAndVerify({
          file,
          setting,
          session,
          property,
        });

        const actionPayloadBase = {
          subjectId,
          awaiting: false,
        };
        const actionPayload =
          result.type === FILE_UPLOAD_RESULT_TYPE.success
            ? {
                ...actionPayloadBase,
                thumbnail: result.thumbnail,
              }
            : actionPayloadBase;
        dispatch(updateFileUploadItemState(actionPayload));

        return result;
      },
      [dispatch, property, subjectId],
    );
  const deleteFromTemporaryFile: UseFileUploadEventHandlerReturnType['deleteFromTemporaryFile'] =
    useCallback(
      async ({ session, setting }) => {
        dispatch(
          updateFileUploadItemState({
            subjectId,
            awaiting: true,
          }),
        );

        const result = await deleteFromTemporary({
          session,
          setting,
          property,
        });
        const actionPayloadBase = {
          subjectId,
          awaiting: false,
        };
        const actionPayload =
          result.type === FILE_UPLOAD_RESULT_TYPE.success
            ? {
                ...actionPayloadBase,
                thumbnail: undefined,
              }
            : actionPayloadBase;
        dispatch(updateFileUploadItemState(actionPayload));

        return result;
      },
      [dispatch, property, subjectId],
    );

  return {
    uploadAndVerifyFile,
    deleteFromTemporaryFile,
  };
};
